import React, { useEffect, useState } from 'react';
import './AddDriver.scss';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import { FLEET_DRIVERS_URL, FLEET_CARS_URL } from '../../../api/urls';
import { useApiFetch } from '../../../api/fetcher';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { AddNewDriver, AddNewDriverCar } from '../../../types/fleetCarTypes';
import { InputMask } from "primereact/inputmask";
import { getPojazdLabel } from '../../../utils/cars';
import { translateApiError } from '../../../utils/apiErrorTranslations';

const AddDriver: React.FC = () => {
    const navigate = useNavigate();
    const apiFetch = useApiFetch();
    const toast = React.useRef<Toast>(null);

    // Step management
    const [activeStep, setActiveStep] = useState(0);
    const [loading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [driverData, setDriverData] = useState<AddNewDriver>({
        phone_number: '',
        first_name: ''
    });
    const [selectedCarIds, setSelectedCarIds] = useState<number[]>([]);

    const [cars, setCars] = useState<AddNewDriverCar[]>([]);
    const [carsLoading, setCarsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const goBackToFleet = () => navigate('/fleet/', { state: { activeTab: 'drivers' } });

    const steps = [
        { label: 'Wprowadź dane kierowcy', eyebrow: 'Krok 1' },
        { label: 'Przypisz pojazd', eyebrow: 'Krok 2' },
        { label: 'Potwierdź dane', eyebrow: 'Krok 3' }
    ];

    useEffect(() => {
        if (activeStep === 1) {
            loadCars();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep]);

    const loadCars = async () => {
        setCarsLoading(true);
        try {
            const res = await apiFetch(FLEET_CARS_URL, { credentials: 'include' });
            const json = await res.json();
            const carsData: AddNewDriverCar[] = Array.isArray(json?.results) ? json.results : (Array.isArray(json) ? json : []);
            setCars(carsData);
        } catch (error) {
            console.error('Error loading cars:', error);
        } finally {
            setCarsLoading(false);
        }
    };

    const handleNext = () => {
        if (activeStep < steps.length - 1) {
            setActiveStep(activeStep + 1);
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep(activeStep - 1);
        }
    };

    const handleCarSelection = (carId: number, checked: boolean) => {
        setSelectedCarIds(prev =>
            checked ? [...prev, carId] : prev.filter(id => id !== carId)
        );
    };

    const handleSaveDriver = async () => {
        setSaving(true);
        const payload_phone_number = "+48" + driverData.phone_number.replace(/^\(\+48\)\s?/, '').replace(/-/g, '');
        try {
            const payload = {
                phone_number: payload_phone_number,
                first_name: driverData.first_name,
                car_ids: selectedCarIds
            };

            const response = await apiFetch(FLEET_DRIVERS_URL, {
                method: 'POST',
                body: JSON.stringify(payload),
                credentials: 'include',
            });

            if (response.ok) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Sukces',
                    detail: 'Dodano nowego kierowcę',
                    life: 3000
                });
                setTimeout(() => {
                    navigate('/fleet/', { state: { activeTab: 'drivers' } });
                }, 1000);
            } else {
                const errorData = await response.json();
                const phoneMsg = Array.isArray(errorData?.phone_number) ? errorData.phone_number[0] : undefined;
                const errorMessage = phoneMsg
                    ? translateApiError(phoneMsg, 'Nieprawidłowy numer telefonu.')
                    : errorData?.detail || 'Wystąpił błąd podczas dodawania kierowcy';
                toast.current?.show({
                    severity: 'error',
                    summary: 'Błąd',
                    detail: errorMessage,
                    life: 5000
                });
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Błąd',
                detail: 'Wystąpił błąd podczas dodawania kierowcy',
                life: 5000
            });
        } finally {
            setSaving(false);
        }
    };

    const filteredCars = cars.filter(car =>
        car.plate.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${car.maker_name} ${car.model_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const renderStep1 = () => (
        <div className="add-driver-step-content">
            <div className="add-driver-step-content__intro">
                <div className="add-driver-step-content__icon-circle">
                    <i className="pi pi-user" />
                </div>
                <div className="add-driver-step-content__heading">
                    <h2>Dane kierowcy</h2>
                    <p>Wprowadź podstawowe informacje o nowym kierowcy</p>
                </div>
            </div>

            <div className="add-driver-form">
                <div className="add-driver-form__field">
                    <label htmlFor="phone">Numer telefonu*</label>
                    <InputMask
                        value={driverData.phone_number}
                        onChange={(e) => setDriverData(prev => ({ ...prev, phone_number: e.target.value ? e.target.value : '' }))}
                        id="phone"
                        mask="(+48) 999-999-999"
                        placeholder="(+48) 999-999-999"
                    />
                </div>

                <div className="add-driver-form__field">
                    <label htmlFor="name">Nazwa kierowcy</label>
                    <InputText
                        id="name"
                        value={driverData.first_name}
                        onChange={(e) => setDriverData(prev => ({ ...prev, first_name: e.target.value }))}
                        placeholder="Nazwa kierowcy"
                        className="w-100"
                    />
                </div>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="add-driver-step-content">
            <div className="add-driver-step-content__intro">
                <div className="add-driver-step-content__icon-circle">
                    <i className="pi pi-car" />
                </div>
                <div className="add-driver-step-content__heading">
                    <h2>Przypisz pojazdy do kierowcy</h2>
                    <p>Wybierz pojazdy, do których kierowca będzie miał dostęp</p>
                </div>
            </div>

            <div className="add-driver-vehicles">
                <div className="add-driver-vehicles__search-box">
                    <InputText
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Szukaj"
                        className="w-100"
                    />
                    <span className="add-driver-vehicles__count">
                        Znaleziono {cars.length} {getPojazdLabel(cars.length)} • {selectedCarIds.length} wybrano
                    </span>
                </div>

                {carsLoading ? (
                    <div className="text-center py-4">
                        <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                    </div>
                ) : (
                    <div className="add-driver-vehicles__table">
                        <div className="add-driver-vehicles__row add-driver-vehicles__row--header">
                            <div className="add-driver-vehicles__cell add-driver-vehicles__cell--checkbox" />
                            <div className="add-driver-vehicles__cell">Numer rejestracyjny</div>
                            <div className="add-driver-vehicles__cell">Marka i model</div>
                        </div>
                        {filteredCars.map((car) => (
                            <div key={car.id} className="add-driver-vehicles__row">
                                <div className="add-driver-vehicles__cell add-driver-vehicles__cell--checkbox">
                                    <Checkbox
                                        checked={selectedCarIds.includes(car.id)}
                                        onChange={(e) => handleCarSelection(car.id, e.checked || false)}
                                    />
                                </div>
                                <div className="add-driver-vehicles__cell add-driver-vehicles__cell--plate">{car.plate}</div>
                                <div className="add-driver-vehicles__cell">{`${car.maker_name} ${car.model_name}`}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    const renderStep3 = () => (
        <div className="add-driver-step-content">
            <div className="add-driver-step-content__intro">
                <div className="add-driver-step-content__icon-circle add-driver-step-content__icon-circle--done">
                    <i className="pi pi-check" />
                </div>
                <div className="add-driver-step-content__heading">
                    <h2>Potwierdź dane</h2>
                    <p>Sprawdź wprowadzone informacje przed dodaniem kierowcy</p>
                </div>
            </div>

            <div className="add-driver-summary">
                <h3 className="add-driver-summary__title">Informacje o kierowcy</h3>
                <div className="add-driver-summary__card">
                    <div className="add-driver-summary__row">
                        <span className="add-driver-summary__label">Nazwa kierowcy</span>
                        <span className="add-driver-summary__value">{driverData.first_name}</span>
                    </div>
                    <div className="add-driver-summary__row">
                        <span className="add-driver-summary__label">Numer telefonu</span>
                        <span className="add-driver-summary__value">{driverData.phone_number}</span>
                    </div>
                </div>
            </div>

            <div className="add-driver-summary">
                <div className="add-driver-summary__header">
                    <h3 className="add-driver-summary__title">Przypisane pojazdy</h3>
                    <span className="add-driver-summary__count">{selectedCarIds.length} {getPojazdLabel(selectedCarIds.length)}</span>
                </div>
                <div className="add-driver-summary__card">
                    {selectedCarIds.length > 0 ? (
                        cars
                            .filter(car => selectedCarIds.includes(car.id))
                            .map((car) => (
                                <div key={car.id} className="add-driver-summary__row">
                                    <span className="add-driver-summary__value">{car.plate}</span>
                                    <span className="add-driver-summary__label">{`${car.maker_name} ${car.model_name}`}</span>
                                </div>
                            ))
                    ) : (
                        <p className="add-driver-summary__empty">Brak przypisanych pojazdów</p>
                    )}
                </div>
            </div>
        </div>
    );

    const renderCurrentStep = () => {
        switch (activeStep) {
            case 0:
                return renderStep1();
            case 1:
                return renderStep2();
            case 2:
                return renderStep3();
            default:
                return renderStep1();
        }
    };

    const canProceed = () => {
        switch (activeStep) {
            case 0:
                return driverData.phone_number.trim() && driverData.first_name.trim();
            case 1:
                return true;
            case 2:
                return true;
            default:
                return false;
        }
    };

    return (
        <div className="add-driver-page pp-font-geist card-margins panel-margin-top">
            <Toast ref={toast} />

            <div className="add-driver-page__header">
                <div className="add-driver-page__header-left">
                    <button
                        type="button"
                        className="add-driver-page__back"
                        onClick={goBackToFleet}
                        aria-label="Wróć do floty"
                    >
                        <i className="pi pi-arrow-left" />
                    </button>
                    <div>
                        <h1 className="add-driver-page__title">Nowy kierowca</h1>
                        <p className="add-driver-page__subtitle">Stwórz nowego kierowcę</p>
                    </div>
                </div>
                <Button label="Anuluj" className="add-driver-page__cancel-btn" onClick={goBackToFleet} />
            </div>

            <div className="add-driver-page__card">
                <div className="add-driver-stepper">
                    {steps.map((step, idx) => {
                        const status = idx < activeStep ? 'done' : idx === activeStep ? 'active' : 'upcoming';
                        return (
                            <div key={step.label} className={`add-driver-step add-driver-step--${status}`}>
                                <div className="add-driver-step__head">
                                    <i
                                        className={`pi add-driver-step__icon ${status === 'done' ? 'pi-check-circle' : status === 'active' ? 'pi-circle-fill' : 'pi-circle'
                                            }`}
                                    />
                                    <span className="add-driver-step__eyebrow">{step.eyebrow}</span>
                                </div>
                                <span className="add-driver-step__label">{step.label}</span>
                            </div>
                        );
                    })}
                </div>

                <div className="add-driver-page__content">
                    {renderCurrentStep()}
                </div>
            </div>

            <div className="add-driver-page__footer">
                {activeStep > 0 && (
                    <Button
                        label="Wstecz"
                        icon="pi pi-arrow-left"
                        className="add-driver-page__back-btn"
                        onClick={handleBack}
                        disabled={loading || saving}
                    />
                )}

                {activeStep < steps.length - 1 ? (
                    <Button
                        label="Dalej"
                        className="add-driver-page__next-btn"
                        icon="pi pi-arrow-right"
                        iconPos="right"
                        onClick={handleNext}
                        disabled={!canProceed() || loading || saving}
                    />
                ) : (
                    <Button
                        label="Potwierdź i dodaj kierowce"
                        className="add-driver-page__next-btn"
                        iconPos="right"
                        onClick={handleSaveDriver}
                        loading={saving}
                        disabled={!canProceed() || loading}
                    />
                )}
            </div>
        </div>
    );
};

export default AddDriver;
